import { Btn, SectionHeading, Stats, TeamCard, useText } from '@kkf/design'
import { ABOUT, ABOUT_TABS, ABOUT_TEAM_HEADING, STATS } from '../data/about'
import { TEAM_MEMBERS_HOME } from '../data/team'
import { useT } from '../i18n'

/**
 * The about page.
 *
 * **No banner (2026-09-19).** It opened on `Breadcrumb`'s dark photographic band carrying
 * "About Us" and the trail Home › About Us — a full screen-height of chrome that repeated
 * the nav entry the visitor had just clicked and said nothing else. The page starts on its
 * own content instead.
 *
 * Two things had to move with it, and both are easy to forget:
 *
 *  - `.ul-plain-page-top` on the first section. The header is absolutely positioned, so
 *    without it the bar sits on top of the first heading. It also draws the wash that gives
 *    the floating bar something to be legible against.
 *  - **The `<h1>`.** `Breadcrumb`'s title was this page's only one, and `audit:seo` fails a
 *    page with any number other than one. The about section's own title is the heading now,
 *    which is what it should always have been — it is what the page is about.
 *
 * `/about` is in this app's `header.plainRoutes` for the same reason. See `SiteShell`.
 */
export function AboutPage() {
  const t = useT()
  const s = useText()
  const a = ABOUT
  const tabs = ABOUT_TABS

  return (
    <>
      {/* ABOUT */}
      <section className="ul-about ul-section-spacing ul-plain-page-top wow animate__fadeInUp">
        <div className="ul-container">
          {/*
            Explicit column widths rather than `row-cols-md-2` (2026-09-21).

            Two equal columns is what a 690x612 photograph wanted. What is in the left one
            is a logo, and what is in the right one is three groups of copy — so the mark
            takes a third and the words take two. `_about.scss` caps the mark well under
            even that, for the same reason.
          */}
          <div className="row align-items-center gy-4 ul-about-row">
            <div className="col-lg-4 col-md-5">
              {/* The Foundation's full mark, not a photograph — see `data/about`. The
                  modifier is what keeps it off the template's two decorative swirls and
                  at a size a logo is read at rather than at the width of its column. */}
              <div className="ul-about-imgs ul-about-imgs--mark">
                <div className="img-wrapper">
                  <img src={a.images.main} alt={s(a.images.mainAlt)} />
                </div>
                <div className="ul-about-imgs-vectors">
                  <img src={a.images.vector1} alt={t('alt.generic')} className="vector-1" />
                  <img src={a.images.vector2} alt={t('alt.generic')} className="vector-2" />
                </div>
              </div>
            </div>

            {/* txt */}
            <div className="col-lg-8 col-md-7">
              <div className="ul-about-txt">
                <span className="ul-section-sub-title ul-section-sub-title--2">{s(a.subTitle)}</span>
                {/* The page's one `<h1>`. It was an `<h2>` under `Breadcrumb`'s title. */}
                <h1 className="ul-section-title">{s(a.title)}</h1>
                <p className="ul-section-descr">{s(a.descr)}</p>

                {/*
                  WHAT WE BELIEVE, WHERE OUR HEART IS, AND THE THREE PILLARS (2026-09-21).

                  This replaced "The Need" — four bullets of what is wrong in Mongolia,
                  beside a photograph, above a button to the resort and the Foundation's
                  inbox at heading size. The Foundation asked for what it believes in that
                  position instead, which makes the page's first screen what this
                  organisation is *for* rather than what is wrong with the place it works
                  in. See `data/about`.
                */}
                <div className="ul-about-belief">
                  <h3 className="ul-about-belief-title">{s(a.belief.title)}</h3>
                  <p className="ul-section-descr">{s(a.belief.body)}</p>
                </div>

                <div className="ul-about-belief">
                  <h3 className="ul-about-belief-title">{s(a.heart.title)}</h3>
                  {a.heart.paragraphs.map((paragraph) => (
                    <p className="ul-section-descr" key={paragraph.en}>
                      {s(paragraph)}
                    </p>
                  ))}
                </div>

                <div className="ul-about-pillars">
                  <h3 className="ul-about-belief-title">{s(a.pillars.title)}</h3>
                  <ul className="block-list">
                    {a.pillars.items.map((pillar) => (
                      <li key={pillar.name.en}>
                        {/* One `<span>`, not two siblings. `.block-list li` is a flex
                            container so the tick can hang beside a wrapped line, and a
                            flex container strips the whitespace between its items — which
                            rendered "Purpose— We work" with no space before the dash. */}
                        <span>
                          <strong>{s(pillar.name)}</strong> — {s(pillar.descr)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* vector */}
        <div className="ul-about-vectors">
          {/* Decorative: an empty `alt` is the correct one, and it needs no
              translation. "vector" was the template's, and a screen reader read it
              aloud as though it meant something. */}
          <img src={a.images.sectionVector} alt="" className="vector-1" />
        </div>
      </section>

      {/*
        MISSION, VISION, STORY — all three are on the page at once: the mission across the
        top, the other two side by side beneath it. They used to be tabs, which hid two
        thirds of the section behind a click.
      */}
      <section className="ul-about-tabs ul-events ul-section-spacing pt-0">
        <div className="ul-container">
          {/* heading */}
          {/* No eyebrow: it read "Who we are" and was invisible — the working blue on a
              near-black band. See `data/about`. */}
          <SectionHeading
            className="align-items-center wow animate__fadeInUp"
            innerClassName="left"
            title={s(tabs.heading.title)}
            titleClassName="text-white"
            aside={
              <Btn to={tabs.heading.ctaTo} icon="fast-forward-double-right-arrows-symbol">
                {s(tabs.heading.ctaLabel)}
              </Btn>
            }
          />

          {/*
            Four equal columns — a two-up grid, which on a wide screen is Story and Mission
            over Vision and What We Do.

            **It was positional and then it was a flag, and it is neither now (2026-09-21).**
            `i === 0` gave the first panel the full row, which was right for three panels and
            wrong for four; a `wide` flag then gave the full row to What We Do, and the
            Foundation asked for that one beside the vision instead. Four panels of equal
            weight need no rule at all, which is the best version of this the file has had.

            `--stacked` went the same way. Every panel is one flow with a small floated
            picture now, so there is no arrangement left for a modifier to choose between.

            What renders *inside* a panel is still chosen by its `id`, so `data/about` can
            reorder them freely.
          */}
          <div className="row ul-bs-row gy-4">
            {tabs.panels.map((panel) => (
              <div className="col-lg-6" key={panel.id}>
                <div className="ul-about-panel">
                  {/*
                    **One flow, with the photograph floated into it (2026-09-21).** The
                    picture is small and on the left; the heading and the prose run down its
                    right-hand side and continue full width underneath it once they pass its
                    bottom edge — an article, not a card with a picture on top of it. It is
                    what takes the least room for the most words, which is what these four
                    panels are for.

                    **`.ul-about-tab-txt` is gone, and it had to be.** A float shortens only
                    the line boxes it sits beside; a wrapper is a block box starting at the
                    container's left edge, so the picture sat on top of it. Everything below
                    is therefore ONE sibling list, in reading order, with nothing grouping
                    the heading and the copy. `_about.scss` carries the rest, including
                    which blocks have to clear the float. Same construction, and the same
                    reason, as `@kkf/design`'s `BlogArticle`.
                  */}
                  <div className="ul-about-tab">
                    <div className="ul-about-tab-img">
                      <img src={panel.image} alt={s(panel.imageAlt)} />
                    </div>

                    <h3 className="ul-about-tab-title">{s(panel.title)}</h3>
                    <p className="ul-about-tab-descr">{s(panel.descr)}</p>

                    {/* The seven areas of our work, and what the revenue pays for. They
                        were inside the mission panel until 2026-09-21, which made it four
                        things in the panel whose job is to say one. */}
                    {panel.id === 'work' && 'closing' in panel && (
                      <>
                        <ul className="ul-about-tab-list">
                          {tabs.missionList.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                        <p className="ul-about-tab-descr">{panel.closing}</p>
                      </>
                    )}
                    {panel.id === 'story' && 'second' in panel && (
                      <>
                        <p className="ul-about-tab-descr">{panel.second}</p>
                        <p className="ul-about-tab-descr">{panel.third}</p>
                        <p className="ul-about-tab-descr">{panel.fourth}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* vectors */}
          <div className="ul-events-vectors">
            <img src={tabs.vectorImage} alt={t('alt.event')} className="vector-2" />
          </div>
        </div>
      </section>

      {/*
        THE FOUR COMMITMENTS — below the story, not above it (2026-09-21).

        They sat between the About section and the mission-and-story block, at a quarter of
        the container each, which is a full screen of circles wedged between two sections
        that read on from each other. They are a summary of what has just been said, so they
        belong after it — and they are capped small now, which is what makes that possible.
        See `sections/_stats.scss`.
      */}
      <Stats items={STATS.map((stat) => ({ ...stat, txt: s(stat.txt) }))} />

      {/* TEAM */}
      <section className="ul-team ul-section-spacing">
        <div className="ul-container">
          {/* Heading */}
          <SectionHeading
            className="justify-content-between"
            innerClassName="left"
            subTitle={s(ABOUT_TEAM_HEADING.subTitle)}
            title={s(ABOUT_TEAM_HEADING.title)}
            aside={
              <div>
                <Btn to={ABOUT_TEAM_HEADING.ctaTo} icon="fast-forward-double-right-arrows-symbol">
                  {s(ABOUT_TEAM_HEADING.ctaLabel)}
                </Btn>
              </div>
            }
          />
          <div className="row row-cols-md-4 row-cols-sm-3 row-cols-2 row-cols-xxs-1 ul-team-row justify-content-center">
            {TEAM_MEMBERS_HOME.map((member) => (
              <div className="col" key={member.image}>
                <TeamCard {...member} />
              </div>
            ))}
          </div>
        </div>
      </section>

    </>
  )
}
